import React, { useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { CircleCheck, CirclePlus, Cog } from 'lucide-react'
import { cn } from '@/lib/utils'
import useFeeEstimates, { useSelectFeeRate } from '@/utils/hooks/useFeeEstimates'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'

const FeeSelector = (props: any) => {
	// `contentClassName` reaches the PORTALED popover panel — surfaces with
	// scoped theme tokens (the /alkanes app's `.alkanes-ink`) pass it because
	// the portal renders outside their scope and would otherwise flash the
	// site theme. `align` forwards for edge-pinned triggers (a status bar's
	// gear would otherwise overflow the viewport).
	const { children, contentClassName, align } = props
	const { speed, selected } = useFeeEstimates()
	// useExchangeRate() kept for subscription side-effects; value unused.
	useExchangeRate()
	const selectFee = useSelectFeeRate()
	const [_k, _setK] = useState(() => new Date().toISOString())

	const feeOptions = [
		{ level: 'Slow', rate: speed.slow, key: 'slow' },
		{ level: 'Medium', rate: speed.medium, key: 'medium' },
		{ level: 'Fast', rate: speed.fast, key: 'fast' }
	]

	const handleSelect = React.useCallback(
		(v: any) => {
			selectFee(v.key)
		},
		[selectFee]
	)

	return (
		<Popover>
			<PopoverTrigger asChild>
				{children || <Cog className="cursor-pointer size-4" />}
			</PopoverTrigger>
			<PopoverContent align={align} className={cn('w-80', contentClassName)}>
				<div className="space-y-2">
					<h4 className="font-medium leading-none">Network fee</h4>
					<div className="space-y-2">
						{feeOptions.map((option) => (
							<div
								key={option.level}
								className="flex items-center cursor-pointer hover:bg-accent rounded-sm p-2"
								onClick={() => handleSelect(option)}
								role="button"
								aria-pressed={option.key === selected}
								tabIndex={0}
								onKeyDown={onKeyboardClick(() => handleSelect(option))}
							>
								{/* The row is the control; the circle only shows which row is
								    chosen. A nested SelectButton here was a second, dead button
								    (its handler was a no-op that also stopped the row's click)
								    and its "Select item" label prefixed every row's name. */}
								{option.key === selected ? (
									<CircleCheck className="size-5 shrink-0 stroke-violet-500" aria-hidden="true" />
								) : (
									<CirclePlus className="size-5 shrink-0 stroke-zinc-400" aria-hidden="true" />
								)}
								<div className="flex flex-col ml-2">
									<span className="text-sm">{option.level}</span>
									<span className="text-xs text-muted-foreground">
									{option.rate} {Number(option.rate) === 1 ? 'sat' : 'sats'}/vB
								</span>
								</div>
							</div>
						))}
					</div>
				</div>
			</PopoverContent>
		</Popover>
	)
}

export default FeeSelector
